import { pipe } from 'fp-ts/function';
import { array, record } from 'fp-ts';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import { DropdownItem, IconName, Stack, Typography, Icon, Tooltip } from '@optimus-web/core';
import { TooltippedIcon } from '@mews-commander/ui';
import { localizeText, normalizeSearchValue } from '@mews-commander/utils';
import { ConfigurationTooltip, getTooltipLabel } from './tooltip/mapTooltipText';
import { ConfigurationValueOption, configurationValueOptionsData } from './utils';
import { TabItemValue } from './CheckInTabs/enums';

export interface FieldData {
    type: CheckInConfigurationType;
    documentType?: CheckInConfigurationType;
    isDisabled?: boolean;
    visibilityOverride?: CheckInConfigurationValue;
    textKey: Mews.TextKey;
}

export const generalSection: FieldData[] = [
    { type: CheckInConfigurationType.Sex, textKey: TextKey.Sex },
    { type: CheckInConfigurationType.Nationality, textKey: TextKey.Nationality },
    { type: CheckInConfigurationType.Phone, textKey: TextKey.Phone },
    { type: CheckInConfigurationType.BirthDate, textKey: TextKey.DateOfBirth },
    { type: CheckInConfigurationType.BirthPlace, textKey: TextKey.BirthPlace },
    { type: CheckInConfigurationType.Occupation, textKey: TextKey.Occupation },
    { type: CheckInConfigurationType.PurposeOfStay, textKey: TextKey.PurposeOfStay },
    { type: CheckInConfigurationType.DietaryRequirements, textKey: TextKey.GuestProfileDietaryRequirements },
    { type: CheckInConfigurationType.CarRegistrationNumber, textKey: TextKey.CarRegistrationNumber },
    {
        type: CheckInConfigurationType.RelationshipToReservationOwner,
        textKey: TextKey.CheckInFieldRelationshipToReservationOwner,
    },
];

export const extendedGeneralSection: FieldData[] = [
    { type: CheckInConfigurationType.FirstName, textKey: TextKey.FirstName },
    { type: CheckInConfigurationType.LastName, textKey: TextKey.LastName },
    { type: CheckInConfigurationType.SecondLastName, textKey: TextKey.SecondLastName },
    { type: CheckInConfigurationType.Email, textKey: TextKey.Email },
    { type: CheckInConfigurationType.Signature, textKey: TextKey.Signature },
    { type: CheckInConfigurationType.Sex, textKey: TextKey.Sex },
    { type: CheckInConfigurationType.Nationality, textKey: TextKey.Nationality },
    { type: CheckInConfigurationType.Phone, textKey: TextKey.Phone },
    { type: CheckInConfigurationType.BirthDate, textKey: TextKey.DateOfBirth },
    { type: CheckInConfigurationType.BirthPlace, textKey: TextKey.BirthPlace },
    { type: CheckInConfigurationType.Occupation, textKey: TextKey.Occupation },
    { type: CheckInConfigurationType.PurposeOfStay, textKey: TextKey.PurposeOfStay },
    { type: CheckInConfigurationType.DietaryRequirements, textKey: TextKey.GuestProfileDietaryRequirements },
    { type: CheckInConfigurationType.CarRegistrationNumber, textKey: TextKey.CarRegistrationNumber },
    {
        type: CheckInConfigurationType.RelationshipToReservationOwner,
        textKey: TextKey.CheckInFieldRelationshipToReservationOwner,
    },
];

export const getGeneralSection = (areExtendedHotelConfiguredFieldsEnabled: boolean): FieldData[] =>
    areExtendedHotelConfiguredFieldsEnabled ? extendedGeneralSection : generalSection;

export const addressSection: FieldData[] = [
    { type: CheckInConfigurationType.AddressLine, textKey: TextKey.AddressLine1 },
    { type: CheckInConfigurationType.AddressCity, textKey: TextKey.City },
    { type: CheckInConfigurationType.AddressPostalCode, textKey: TextKey.PostalCode },
    { type: CheckInConfigurationType.AddressCountry, textKey: TextKey.Country },
    { type: CheckInConfigurationType.AddressCountrySubdivision, textKey: TextKey.CountrySubdivision },
];

export const guestVerificationSection: FieldData[] = [
    { type: CheckInConfigurationType.VerificationPhoto, textKey: TextKey.VerificationPhoto },
    { type: CheckInConfigurationType.IdPhotos, textKey: TextKey.IdPhotos },
    { type: CheckInConfigurationType.IdVerification, textKey: TextKey.IdVerification },
];
interface GetGuestVerificationSectionProps {
    guestVerificationPhotoEnabled: boolean;
    isIdentityDocumentScanEnabled: boolean;
    isIdentityDocumentScanVerificationEnabled: boolean;
}

export const getGuestVerificationSection = ({
    guestVerificationPhotoEnabled,
    isIdentityDocumentScanEnabled,
    isIdentityDocumentScanVerificationEnabled,
}: GetGuestVerificationSectionProps): FieldData[] =>
    guestVerificationSection.filter(
        ({ type }) =>
            (guestVerificationPhotoEnabled && type === CheckInConfigurationType.VerificationPhoto) ||
            (isIdentityDocumentScanEnabled && type === CheckInConfigurationType.IdPhotos) ||
            (isIdentityDocumentScanVerificationEnabled && type === CheckInConfigurationType.IdVerification)
    );

export const identityCardSection: FieldData[] = [
    {
        type: CheckInConfigurationType.IdentityCardIssuingCountry,
        documentType: CheckInConfigurationType.DirectIdentityCard,
        textKey: TextKey.IssuingCountry,
    },
    {
        type: CheckInConfigurationType.IdentityCardIssuingCity,
        documentType: CheckInConfigurationType.DirectIdentityCard,
        textKey: TextKey.IssuingCity,
    },
    {
        type: CheckInConfigurationType.IdentityCardIssueDate,
        documentType: CheckInConfigurationType.DirectIdentityCard,
        textKey: TextKey.IssueDate,
    },
    {
        type: CheckInConfigurationType.IdentityCardExpirationDate,
        documentType: CheckInConfigurationType.DirectIdentityCard,
        textKey: TextKey.ExpirationDate,
    },
];

export const passportSection: FieldData[] = [
    {
        type: CheckInConfigurationType.PassportIssuingCountry,
        textKey: TextKey.IssuingCountry,
        documentType: CheckInConfigurationType.DirectPassport,
    },
    {
        type: CheckInConfigurationType.PassportIssuingCity,
        textKey: TextKey.IssuingCity,
        documentType: CheckInConfigurationType.DirectPassport,
    },
    {
        type: CheckInConfigurationType.PassportIssueDate,
        textKey: TextKey.IssueDate,
        documentType: CheckInConfigurationType.DirectPassport,
    },
    {
        type: CheckInConfigurationType.PassportExpirationDate,
        textKey: TextKey.ExpirationDate,
        documentType: CheckInConfigurationType.DirectPassport,
    },
];

export const driverLicenseSection: FieldData[] = [
    {
        type: CheckInConfigurationType.DriversLicenseIssuingCountry,
        textKey: TextKey.IssuingCountry,
        documentType: CheckInConfigurationType.DirectDriversLicense,
    },
    {
        type: CheckInConfigurationType.DriversLicenseIssuingCity,
        textKey: TextKey.IssuingCity,
        documentType: CheckInConfigurationType.DirectDriversLicense,
    },
    {
        type: CheckInConfigurationType.DriversLicenseIssueDate,
        textKey: TextKey.IssueDate,
        documentType: CheckInConfigurationType.DirectDriversLicense,
    },
    {
        type: CheckInConfigurationType.DriversLicenseExpirationDate,
        textKey: TextKey.ExpirationDate,
        documentType: CheckInConfigurationType.DirectDriversLicense,
    },
];

export const documentFields = [...driverLicenseSection, ...passportSection, ...identityCardSection];
export const documentSectionFields = [
    CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
    CheckInConfigurationType.PassportOrIdentityCard,
    CheckInConfigurationType.DirectDriversLicense,
    CheckInConfigurationType.DirectIdentityCard,
    CheckInConfigurationType.DirectPassport,
];

export const supportedDocumentTypeOptions: Record<string, Mews.TextKey> = {
    [CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense]: TextKey.IdCardOrPassportDriversLicense,
    [CheckInConfigurationType.PassportOrIdentityCard]: TextKey.IdCardOrPassport,
    [CheckInConfigurationType.DirectPassport]: TextKey.Passport,
    [CheckInConfigurationType.DirectIdentityCard]: TextKey.IdCard,
    [CheckInConfigurationType.DirectDriversLicense]: TextKey.DriversLicense,
};

const documentTypeArtificialNumberFieldData: FieldData[] = documentSectionFields.map((type) => ({
    type,
    textKey: supportedDocumentTypeOptions[type],
}));

export const documentFieldsWithTypes = [...documentFields, ...documentTypeArtificialNumberFieldData];

export const configurationTypeSections: Record<string, TabItemValue> = pipe(
    record.toEntries({
        [TabItemValue.General]: extendedGeneralSection,
        [TabItemValue.Address]: addressSection,
        [TabItemValue.Documents]: [...documentFields, ...documentSectionFields.map((type) => ({ type }))],
        [TabItemValue.GuestVerification]: guestVerificationSection,
    }),
    array.flatMap(([tabItem, fields]) => fields.map((field) => [field.type, tabItem])),
    Object.fromEntries
);

interface ListOptions {
    defaultTooltipValue: ConfigurationTooltip;
    requiredTooltipValue?: ConfigurationTooltip;
    fieldName: string | null;
    isDisabled: boolean;
}
interface GetLabelContentProps {
    option: ConfigurationValueOption;
    tooltipDefault: string | null;
    tooltipRequired: string | null;
    isDisabled: boolean;
}

const TooltipedButton = Tooltip(Icon);

const getLabelContent = ({ option, tooltipDefault, tooltipRequired, isDisabled }: GetLabelContentProps) => {
    if (option.value === CheckInConfigurationValue.Default && tooltipDefault != null) {
        return (
            <Stack itemAlignment="center" spacing="50">
                <LocalizedText textKey={option.textKey} />
                <TooltippedIcon
                    size="small"
                    iconVariant={isDisabled ? 'inherit' : 'info'}
                    value={IconName.Info}
                    tooltip={tooltipDefault}
                />
            </Stack>
        );
    }

    if (
        (option.value === CheckInConfigurationValue.Required || option.value === CheckInConfigurationValue.Optional) &&
        tooltipRequired != null
    ) {
        return (
            <Stack itemAlignment="center" spacing="50">
                <LocalizedText textKey={option.textKey} />
                <TooltipedButton
                    tooltip={tooltipRequired}
                    size="small"
                    value={IconName.Problematic}
                    variant={isDisabled ? 'inherit' : 'warning'}
                />
            </Stack>
        );
    }

    return <LocalizedText textKey={option.textKey} />;
};

export const listOptions = ({ defaultTooltipValue, requiredTooltipValue, fieldName, isDisabled }: ListOptions) => {
    const tooltipDefault = fieldName != null ? getTooltipLabel({ tooltipValue: defaultTooltipValue, fieldName }) : null;
    const tooltipRequired =
        fieldName != null && requiredTooltipValue
            ? getTooltipLabel({ tooltipValue: requiredTooltipValue, fieldName })
            : null;

    return pipe(
        configurationValueOptionsData,
        array.map((option) => {
            const labelContent = getLabelContent({ option, tooltipDefault, tooltipRequired, isDisabled });

            return (
                <DropdownItem
                    label={
                        <Typography variant={isDisabled ? 'secondary' : 'primary'} textStyle="bodyMedium" as="span">
                            {labelContent}
                        </Typography>
                    }
                    key={option.value}
                    searchValue={pipe(option.textKey, localizeText, normalizeSearchValue)}
                    value={option.value}
                />
            );
        })
    );
};
